declare const process: { env: Record<string, string | undefined> };

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_PAYMENT_API_URL ||
  'https://bookapt-payment-api.onrender.com';

export type DeliveryPublicConfig = {
  serviceFeeCents: number;
  deliveryEnabled: boolean;
  currency: string;
};

let cachedConfig: DeliveryPublicConfig | null = null;
let inflight: Promise<DeliveryPublicConfig> | null = null;

const FALLBACK_CONFIG: DeliveryPublicConfig = {
  serviceFeeCents: 25,
  deliveryEnabled: true,
  currency: 'USD',
};

/** Load delivery settings from payment API (DELIVERY_SERVICE_FEE_CENTS on Render). */
export async function fetchDeliveryPublicConfig(
  options?: { force?: boolean }
): Promise<DeliveryPublicConfig> {
  if (cachedConfig && !options?.force) {
    return cachedConfig;
  }
  if (inflight && !options?.force) {
    return inflight;
  }

  inflight = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/delivery/config`, {
        method: 'GET',
        cache: 'no-store',
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        return { ...FALLBACK_CONFIG };
      }
      const config: DeliveryPublicConfig = {
        serviceFeeCents: Number(data.serviceFeeCents) || FALLBACK_CONFIG.serviceFeeCents,
        deliveryEnabled: data.deliveryEnabled !== false,
        currency: typeof data.currency === 'string' ? data.currency : 'USD',
      };
      cachedConfig = config;
      return config;
    } catch {
      return { ...FALLBACK_CONFIG };
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export async function getDeliveryServiceFeeCents(): Promise<number> {
  const config = await fetchDeliveryPublicConfig();
  return config.serviceFeeCents;
}

/** Prefetch on web widget load; safe no-op if fetch unavailable. */
export function warmupDeliveryConfig(): void {
  if (typeof fetch === 'undefined') return;
  void fetchDeliveryPublicConfig();
}
